import type { Product } from '@/domain/Product.ts';
import MediaGallery from '@/widgets/components/controls/MediaGallery/MediaGallery.tsx';
import SkeletonShape from '@/widgets/components/controls/Skeleton/SkeletonShape.tsx';
import useTranslations from '@/widgets/hooks/useTranslations.tsx';
import type {
  Options,
  Translations
} from '@/widgets/ProductDetailsHero/config';
import {
  trackMediaGalleryFullScreenView,
  trackMediaGalleryImageMagnify,
  trackMediaGalleryImageView
} from '../../Tracking.ts';
import styles from './ProductMediaSection.module.scss';
import { getProductMediaForGallery } from './utils.ts';
import useWidgetOptions from '@/widgets/hooks/useWidgetOptions.tsx';
import useMediaQuery from '@/widgets/hooks/useMediaQuery.tsx';

export type ProductMediaSectionProps = {
  product: Product;
  class?: string;
};
export default function ProductMediaSection({
  product,
  class: className
}: ProductMediaSectionProps) {
  const { t } = useTranslations<Translations>();
  const { disableMediaGalleryVideoLoop } = useWidgetOptions<Options>();
  const { reducedMotion } = useMediaQuery();

  const media = getProductMediaForGallery(product);
  const closeLabel = t('productDetailsHero.mediaGallery.closeLabel');
  const prevLabel = t('productDetailsHero.mediaGallery.chevronLeftLabel');
  const nextLabel = t('productDetailsHero.mediaGallery.chevronRightLabel');
  const imageLabel = t(
    'productDetailsHero.mediaGallery.carouselImageAriaLabel'
  );
  const noVideoSupportLabel = t(
    'productDetailsHero.mediaGallery.noVideoSupportLabel'
  );
  const playPauseButtonLabel = t(
    'productDetailsHero.mediaGallery.playPauseButtonLabel'
  );

  return (
    <section class={className}>
      <MediaGallery
        media={media}
        onCarouselImageViewed={(viewedIndex, totalImages, eventSource) =>
          trackMediaGalleryImageView(viewedIndex, totalImages, eventSource)
        }
        onGalleryImageViewed={(viewedIndex, totalImages, eventSource) =>
          trackMediaGalleryFullScreenView(viewedIndex, totalImages, eventSource)
        }
        onMagnifiedImageViewed={(viewedIndex, totalImages) =>
          trackMediaGalleryImageMagnify(viewedIndex, totalImages)
        }
        config={{
          lazyLoading: {
            carousel: 'auto',
            thumb: 'auto'
          },
          translations: {
            closeLabel,
            prevLabel,
            nextLabel,
            imageLabel,
            noVideoSupportLabel,
            playPauseButtonLabel
          },
          loopVideos: !disableMediaGalleryVideoLoop,
          hasReducedMotion: reducedMotion
        }}
      />
    </section>
  );
}

ProductMediaSection.Skeleton = function Skeleton({
  class: className
}: {
  class?: string;
}) {
  return (
    <section class={className}>
      <SkeletonShape class={styles.skeleton} />
    </section>
  );
};
